import styled, { type CSSProperties } from "styled-components";
import React, { useRef, useEffect, useState } from "react";
import { InputField } from "./Input";
import { Icon } from "../Icon/Icon";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import theme from "@/themes/velys-theme";
import "react-phone-number-input/style.css";
import flags from "react-phone-number-input/flags";
import type { Country } from "react-phone-number-input";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";

const PhoneInputWrapper = styled.div<{
  $countryCode: string;
  $valueExists: boolean;
  $hasSelector: boolean;
}>`
  position: relative;
  max-width: 300px;
  width: 100%;
  min-width: 300px;

  input {
    padding-left: ${(props) =>
      props.$hasSelector
        ? "52px"
        : props.$valueExists
          ? props.$countryCode.length > 1
            ? "40px"
            : "25px"
          : `${20 + props.$countryCode.length * 8}px`};
  }
`;

const ClearButton = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const CountryCodePrefix = styled.span<{
  $color: string;
  $backgroundColor: string;
  $disabled?: boolean;
}>`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.black};
  background-color: ${(props) => props.$backgroundColor};
  font-weight: normal;
  user-select: none;
  pointer-events: none;
  z-index: 1;

  opacity: ${(props) => (props.$disabled ? "0.15" : "1")};

  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
`;

const FlagSelectorWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
`;

const FlagSelectorButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0 5px 0 6px;
  height: 100%;
  background: transparent;
  border: none;
  border-right: 1px solid ${(props) => props.theme.cardBorder};
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  font-size: 0.75rem;
  color: ${(props) => props.theme.black};
  white-space: nowrap;

  .PhoneInputCountryIcon {
    width: 14px !important;
    height: 10px !important;
  }

  &:hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.04);
  }

  @media (max-width: 1024px) {
    font-size: 0.7rem;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  transform: ${(props) => (props.$open ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.15s ease;
  opacity: 0.5;
`;

const FlagDropdown = styled.div`
  background: ${(props) => props.theme.white};
  border: 1px solid ${(props) => props.theme.cardBorder};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 200px;
`;

const FlagOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  background: ${(props) =>
    props.$selected ? `${props.theme.main}15` : "transparent"};
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${(props) => props.theme.black};
  text-align: left;

  .PhoneInputCountryIcon {
    width: 16px !important;
    height: 11px !important;
  }

  &:hover {
    background-color: ${(props) => `${props.theme.main}10`};
  }
`;

interface PhoneInputProps {
  countryCode: string;
  onCountryCodeChange?: (callingCode: string) => void;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  disabled?: boolean;
  color: string;
  backgroundColor: string;
  inputTitle: string;
  styles?: CSSProperties;
  required?: boolean;
  showNA?: boolean;
  onClear?: () => void;
}

export function PhoneInput({
  countryCode,
  onCountryCodeChange,
  value,
  onChange,
  readOnly,
  disabled,
  color,
  backgroundColor,
  inputTitle,
  styles,
  required,
  showNA = true,
  onClear,
}: PhoneInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tCountries = useTranslations("countryLabels");

  const showCountryCode = value !== "N/A" && (!showNA || value !== "");
  const hasValue = value !== "" && value !== "N/A";

  const phoneCountryOptions: Array<{
    country: Country;
    callingCode: string;
    label: string;
  }> = [
    { country: "US", callingCode: "1", label: tCountries("unitedStates") },
    { country: "GB", callingCode: "44", label: tCountries("unitedKingdom") },
    { country: "AU", callingCode: "61", label: tCountries("australia") },
    { country: "AT", callingCode: "43", label: tCountries("austria") },
    { country: "CH", callingCode: "41", label: tCountries("switzerland") },
    { country: "DE", callingCode: "49", label: tCountries("germany") },
    { country: "IE", callingCode: "353", label: tCountries("ireland") },
  ];

  const currentOption = phoneCountryOptions.find(
    (o) => o.callingCode === countryCode
  );
  const FlagComponent = currentOption ? flags[currentOption.country] : null;

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <PhoneInputWrapper
      $countryCode={countryCode}
      $valueExists={value !== "" && value !== "N/A"}
      $hasSelector={!!onCountryCodeChange}
      style={styles}
    >
      {showCountryCode && (
        <>
          {onCountryCodeChange ? (
            <FlagSelectorWrapper>
              <FlagSelectorButton
                ref={buttonRef}
                type="button"
                $disabled={disabled}
                onClick={() => !disabled && handleOpen()}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
              >
                {FlagComponent && (
                  <FlagComponent title={currentOption?.label ?? countryCode} />
                )}
                <span>+{countryCode}</span>
                <Chevron $open={isOpen} />
              </FlagSelectorButton>

              {isOpen &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    style={{
                      position: "fixed",
                      top: dropdownPos.top,
                      left: dropdownPos.left,
                      zIndex: 9999,
                    }}
                  >
                    <FlagDropdown role="listbox">
                      {phoneCountryOptions.map((option) => {
                        const OptionFlag = flags[option.country];
                        return (
                          <FlagOption
                            key={option.country}
                            type="button"
                            $selected={option.callingCode === countryCode}
                            role="option"
                            aria-selected={option.callingCode === countryCode}
                            onClick={() => {
                              onCountryCodeChange(option.callingCode);
                              setIsOpen(false);
                            }}
                          >
                            {OptionFlag && (
                              <div className="PhoneInputCountryIcon">
                                <OptionFlag title={option.label} />
                              </div>
                            )}
                            <span>{option.label}</span>
                            <span style={{ marginLeft: "auto", opacity: 0.6 }}>
                              +{option.callingCode}
                            </span>
                          </FlagOption>
                        );
                      })}
                    </FlagDropdown>
                  </div>,
                  document.body
                )}
            </FlagSelectorWrapper>
          ) : (
            <CountryCodePrefix
              $color={color}
              $backgroundColor={`${backgroundColor}${hasValue ? "5" : ""}`}
              $disabled={disabled}
            >
              +{countryCode}
            </CountryCodePrefix>
          )}
        </>
      )}
      <InputField
        styles={styles}
        maxLength={12}
        value={value === "" && showNA ? "N/A" : value}
        onChange={onChange}
        inputTitle={inputTitle}
        readOnly={readOnly}
        disabled={disabled}
        color={color}
        backgroundColor={`${backgroundColor}30`}
        type="tel"
        required={required}
      />
      {hasValue && onClear && (
        <ClearButton onClick={onClear}>
          <Icon
            url={CloseIcon.src}
            color={theme.main}
            height="14px"
            width="14px"
            onPress={onClear}
          />
        </ClearButton>
      )}
    </PhoneInputWrapper>
  );
}
